// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * AYNI XR Studio — what a stop does around the data (docs/AYNI_XR_STUDIO_MOTION_PLAN.md §4–5),
 * painted on a 2D canvas: the render's compositor draws it into every frame,
 * the Khipu player and the Studio's preview over the live globe.
 *
 * The rule for every effect: **the data inside the focus is never altered.**
 * The spotlight darkens and greys only outside its circle (an even-odd path
 * and a clip keep the inside's pixels byte for byte); the depth blur leaves a
 * sharp centre untouched. Callouts, arrows, the counter and title cards are
 * drawn on top, as captions always were, and the provenance record lists them.
 *
 * Timing is local to the stop's hold: things fade or pop in at its start and
 * leave at its end. Sizes follow the picture's height (1 unit = 1 px at 1080).
 */

import type { KhipuEffects, KhipuText } from '../khipu'
import { MEASUREMENT_COLORS } from '../measure/colors'

export interface EffectsEnv {
  width: number
  height: number
  /** A place on the picture, or null when it is behind the globe. */
  project(lat: number, lon: number): { x: number; y: number } | null
  /** The data value at a place with its unit ("28.4 °C"), or null where there is none. */
  readValue(lat: number, lon: number): string | null
  /** The clean frame (the globe without overlays), for blur and grey; absent live, where CSS does it. */
  source?: CanvasImageSource | null
  /** Makes an offscreen canvas of a size (the render passes one; tests a fake). */
  makeCanvas?(w: number, h: number): HTMLCanvasElement
  /** The language to read a title card in. */
  language: string
  accent: string
  font: string
  /** A video frame (grain and the frame number that moves it), not the live player. */
  frame?: number
  /** Where the counter goes: top left in a video (under the date), top centre live (the app's panels sit at the sides). */
  counterAlign?: 'start' | 'centre'
  /** A split compare drawn on the globe now: the dates either side of its line (absent: the line is not drawn). */
  compareLabels?: { here: string; there: string } | null
}

/** Where the stop is: seconds into its hold, of how long. */
export interface EffectsClock {
  local: number
  hold: number
}

const FADE_IN = 0.5
const FADE_OUT = 0.4
const clamp01 = (v: number) => Math.min(1, Math.max(0, v))

/** 0 → 1 over the first `inS` seconds, 1 → 0 over the last `outS`. */
export function fade(clock: EffectsClock, inS = FADE_IN, outS = FADE_OUT): number {
  const a = inS > 0 ? clock.local / inS : 1
  const b = outS > 0 ? (clock.hold - clock.local) / outS : 1
  return clamp01(Math.min(a, b))
}

/**
 * Where a split compare's line is, across the picture (0 left, 1 right):
 * from `from` to `to` over the stop's hold, eased; a line that is not told
 * to move stays in the middle.
 */
export function compareSplit(c: NonNullable<KhipuEffects['compare']>, clock: EffectsClock): number {
  const from = clamp01(c.from ?? 0.5)
  const to = clamp01(c.to ?? from)
  const p = clock.hold > 0 ? clamp01(clock.local / clock.hold) : 1
  const eased = p * p * (3 - 2 * p)
  return from + (to - from) * eased
}

/** A title card's text in a language, else English, else the first one. */
export function textIn(text: KhipuText | undefined, language: string): string {
  if (!text) return ''
  if (typeof text === 'string') return text
  const t = text as Record<string, string>
  return t[language] ?? t.en ?? Object.values(t)[0] ?? ''
}

/** The spotlight's circle on the picture: centre and radius in pixels, or null when its centre is hidden. */
export function spotlightCircle(spot: NonNullable<KhipuEffects['spotlight']>, env: Pick<EffectsEnv, 'project'>): { x: number; y: number; r: number } | null {
  const c = env.project(spot.lat, spot.lon)
  if (!c) return null
  // The radius as seen: the farthest of four points that far away on the globe (one may be hidden).
  const rad = Math.PI / 180
  let r = 0
  for (const bearing of [0, 90, 180, 270]) {
    const lat1 = spot.lat * rad, lon1 = spot.lon * rad, d = spot.radius * rad, b = bearing * rad
    const lat2 = Math.asin(Math.sin(lat1) * Math.cos(d) + Math.cos(lat1) * Math.sin(d) * Math.cos(b))
    const lon2 = lon1 + Math.atan2(Math.sin(b) * Math.sin(d) * Math.cos(lat1), Math.cos(d) - Math.sin(lat1) * Math.sin(lat2))
    const p = env.project(lat2 / rad, lon2 / rad)
    if (p) r = Math.max(r, Math.hypot(p.x - c.x, p.y - c.y))
  }
  return r > 0 ? { x: c.x, y: c.y, r } : null
}

/** A deterministic grain tile for a frame (the same frame always gets the same grain). */
function grainTile(env: EffectsEnv, seed: number): HTMLCanvasElement | null {
  if (!env.makeCanvas) return null
  const size = 128
  const tile = env.makeCanvas(size, size)
  const g = tile.getContext('2d')
  if (!g) return null
  const img = g.createImageData(size, size)
  let s = (seed * 2654435761) >>> 0
  for (let i = 0; i < img.data.length; i += 4) {
    s = (s ^ (s << 13)) >>> 0; s = (s ^ (s >>> 17)) >>> 0; s = (s ^ (s << 5)) >>> 0
    const v = s & 255
    img.data[i] = img.data[i + 1] = img.data[i + 2] = v
    img.data[i + 3] = 255
  }
  g.putImageData(img, 0, 0)
  return tile
}

/**
 * Paint a stop's effects. Order matters: the surroundings first (depth blur,
 * spotlight, vignette, grain), then what explains (arrows, callouts, counter),
 * then a title card over everything.
 */
export function paintEffects(ctx: CanvasRenderingContext2D, effects: KhipuEffects, clock: EffectsClock, env: EffectsEnv): void {
  const { width: W, height: H } = env
  const unit = H / 1080
  const alpha = fade(clock)

  // Depth blur: a blurred copy outside a sharp centre (needs the clean frame).
  if (effects.depthBlur && effects.depthBlur > 0 && env.source && env.makeCanvas) {
    const blur = env.makeCanvas(W, H)
    const b = blur.getContext('2d')
    if (b) {
      b.filter = `blur(${Math.round(2 + 14 * effects.depthBlur * unit)}px)`
      b.drawImage(env.source, 0, 0, W, H)
      b.filter = 'none'
      // Cut the sharp centre out of the blurred copy: inside r0 nothing of the blur remains.
      const r0 = Math.min(W, H) * 0.3, r1 = Math.min(W, H) * 0.62
      const g = b.createRadialGradient(W / 2, H / 2, r0, W / 2, H / 2, r1)
      g.addColorStop(0, 'rgba(0,0,0,1)')
      g.addColorStop(1, 'rgba(0,0,0,0)')
      b.globalCompositeOperation = 'destination-out'
      b.fillStyle = g
      b.fillRect(0, 0, W, H)
      b.beginPath()
      b.arc(W / 2, H / 2, r0, 0, Math.PI * 2)
      b.fill()
      ctx.save()
      ctx.globalAlpha = alpha
      ctx.drawImage(blur, 0, 0)
      ctx.restore()
    }
  }

  // Spotlight: dim (and grey) outside the circle; the inside keeps its pixels.
  const spot = effects.spotlight ? spotlightCircle(effects.spotlight, env) : null
  if (effects.spotlight && spot) {
    const outside = (c: CanvasRenderingContext2D) => {
      c.beginPath()
      c.rect(0, 0, W, H)
      c.arc(spot.x, spot.y, spot.r, 0, Math.PI * 2, true)
    }
    ctx.save()
    outside(ctx)
    ctx.clip('evenodd')
    if (effects.spotlight.desaturate && env.source) {
      ctx.globalAlpha = alpha
      ctx.filter = 'grayscale(1)'
      ctx.drawImage(env.source, 0, 0, W, H)
      ctx.filter = 'none'
    }
    ctx.globalAlpha = 1
    ctx.fillStyle = `rgba(0, 0, 0, ${(effects.spotlight.dim ?? 0.55) * alpha})`
    ctx.fillRect(0, 0, W, H)
    if (effects.spotlight.outline) {
      ctx.strokeStyle = `rgba(255, 255, 255, ${0.85 * alpha})`
      ctx.lineWidth = 3 * unit
      ctx.shadowColor = env.accent
      ctx.shadowBlur = 16 * unit
      ctx.beginPath()
      ctx.arc(spot.x, spot.y, spot.r + 2 * unit, 0, Math.PI * 2)
      ctx.stroke()
    }
    ctx.restore()
  }

  if (effects.vignette && effects.vignette > 0) {
    const g = ctx.createRadialGradient(W / 2, H / 2, Math.min(W, H) * 0.35, W / 2, H / 2, Math.hypot(W, H) / 2)
    g.addColorStop(0, 'rgba(0,0,0,0)')
    g.addColorStop(1, `rgba(0,0,0,${0.75 * effects.vignette * alpha})`)
    ctx.save()
    if (spot) { ctx.beginPath(); ctx.rect(0, 0, W, H); ctx.arc(spot.x, spot.y, spot.r, 0, Math.PI * 2, true); ctx.clip('evenodd') }
    ctx.fillStyle = g
    ctx.fillRect(0, 0, W, H)
    ctx.restore()
  }

  if (effects.grain && effects.grain > 0 && typeof env.frame === 'number') {
    const tile = grainTile(env, env.frame + 1)
    const pattern = tile ? ctx.createPattern(tile, 'repeat') : null
    if (pattern) {
      ctx.save()
      if (spot) { ctx.beginPath(); ctx.rect(0, 0, W, H); ctx.arc(spot.x, spot.y, spot.r, 0, Math.PI * 2, true); ctx.clip('evenodd') }
      ctx.globalAlpha = 0.12 * effects.grain * alpha
      ctx.globalCompositeOperation = 'overlay'
      ctx.fillStyle = pattern
      ctx.fillRect(0, 0, W, H)
      ctx.restore()
    }
  }

  // Arrows draw themselves over the first 60 % of the hold (1.5 to 3 s), then show their label.
  for (const arrow of effects.arrows ?? []) {
    const drawFor = Math.min(3, Math.max(1.5, clock.hold * 0.6))
    const f = clamp01(clock.local / drawFor)
    const pts = arrow.points.map((p) => env.project(p.lat, p.lon))
    const visible: Array<{ x: number; y: number }> = []
    for (const p of pts) if (p) visible.push(p)
    if (visible.length < 2) continue
    const legs = visible.slice(1).map((p, i) => Math.hypot(p.x - visible[i].x, p.y - visible[i].y))
    const total = legs.reduce((s, d) => s + d, 0)
    let left = total * f
    const colour = MEASUREMENT_COLORS[(arrow.color ?? 0) % MEASUREMENT_COLORS.length]
    ctx.save()
    ctx.globalAlpha = fade(clock, 0.2, FADE_OUT)
    ctx.strokeStyle = colour
    ctx.lineWidth = 5 * unit
    ctx.lineCap = 'round'
    ctx.lineJoin = 'round'
    ctx.shadowColor = 'rgba(0,0,0,0.6)'
    ctx.shadowBlur = 6 * unit
    ctx.beginPath()
    ctx.moveTo(visible[0].x, visible[0].y)
    let tip = visible[0], dir = { x: 1, y: 0 }
    for (let i = 0; i < legs.length && left > 0; i++) {
      const step = Math.min(left, legs[i])
      const k = legs[i] ? step / legs[i] : 1
      const a = visible[i], b = visible[i + 1]
      tip = { x: a.x + (b.x - a.x) * k, y: a.y + (b.y - a.y) * k }
      if (legs[i]) dir = { x: (b.x - a.x) / legs[i], y: (b.y - a.y) / legs[i] }
      ctx.lineTo(tip.x, tip.y)
      left -= step
    }
    ctx.stroke()
    const head = 18 * unit
    ctx.fillStyle = colour
    ctx.beginPath()
    ctx.moveTo(tip.x + dir.x * head * 0.4, tip.y + dir.y * head * 0.4)
    ctx.lineTo(tip.x - dir.x * head - dir.y * head * 0.6, tip.y - dir.y * head + dir.x * head * 0.6)
    ctx.lineTo(tip.x - dir.x * head + dir.y * head * 0.6, tip.y - dir.y * head - dir.x * head * 0.6)
    ctx.closePath()
    ctx.fill()
    if (arrow.label && f >= 1) {
      ctx.shadowBlur = 0
      label(ctx, arrow.label, tip.x + 12 * unit, tip.y - 12 * unit, unit, env.font)
    }
    ctx.restore()
  }

  // Callouts pop in one after another and leave at the end.
  ;(effects.callouts ?? []).forEach((c, i) => {
    const at = env.project(c.lat, c.lon)
    if (!at || at.x < 0 || at.y < 0 || at.x > W || at.y > H) return
    const shifted = { local: clock.local - i * 0.25, hold: clock.hold - i * 0.25 }
    const a = fade(shifted, 0.35, 0.3)
    if (a <= 0) return
    const value = c.value ? env.readValue(c.lat, c.lon) : null
    const text = value ? `${c.label} · ${value}` : c.label
    const dx = 46 * unit, dy = -46 * unit
    ctx.save()
    ctx.globalAlpha = a
    ctx.strokeStyle = '#ffffff'
    ctx.lineWidth = 2.5 * unit
    ctx.beginPath()
    ctx.moveTo(at.x, at.y)
    ctx.lineTo(at.x + dx * a, at.y + dy * a)
    ctx.stroke()
    ctx.fillStyle = '#ffffff'
    ctx.beginPath()
    ctx.arc(at.x, at.y, 5 * unit, 0, Math.PI * 2)
    ctx.fill()
    label(ctx, text, at.x + dx * a + 6 * unit, at.y + dy * a, unit, env.font, env.accent)
    ctx.restore()
  })

  // A split compare: the line between the two dates, and each date on its side.
  if (effects.compare && env.compareLabels) {
    const x = Math.round(compareSplit(effects.compare, clock) * W)
    ctx.save()
    ctx.globalAlpha = alpha
    ctx.fillStyle = '#ffffff'
    ctx.shadowColor = 'rgba(0,0,0,0.6)'
    ctx.shadowBlur = 6 * unit
    // Above the credit and legend at the foot of the picture, which stay clear (rights audit §13 Q10).
    ctx.fillRect(x - 1.5 * unit, H * 0.1, 3 * unit, H * 0.75)
    ctx.shadowBlur = 0
    const size = Math.round(24 * unit)
    const y = H * 0.16
    const gap = 14 * unit
    const leftWidth = measure(ctx, env.compareLabels.here, size, env.font) + 18 * unit
    label(ctx, env.compareLabels.here, x - gap - leftWidth, y, unit, env.font)
    label(ctx, env.compareLabels.there, x + gap, y, unit, env.font, env.accent)
    ctx.restore()
  }

  // The counter: a large reading of the data at a place, under the date.
  if (effects.counter) {
    const value = env.readValue(effects.counter.lat, effects.counter.lon)
    if (value) {
      const size = Math.round(64 * unit)
      ctx.save()
      ctx.globalAlpha = alpha
      ctx.fillStyle = 'rgba(0,0,0,0.55)'
      ctx.font = `700 ${size}px ${env.font}`
      ctx.textBaseline = 'top'
      const small = Math.round(20 * unit)
      const w = Math.max(ctx.measureText(value).width, effects.counter.label ? measure(ctx, effects.counter.label, small, env.font) : 0) + 28 * unit
      const h = size + (effects.counter.label ? small * 1.4 : 0) + 24 * unit
      const x = env.counterAlign === 'centre' ? (W - w) / 2 : W * 0.05
      const y = env.counterAlign === 'centre' ? H * 0.05 : H * 0.05 + 70 * unit
      ctx.fillRect(x, y, w, h)
      ctx.fillStyle = env.accent
      ctx.fillRect(x, y, 6 * unit, h)
      ctx.fillStyle = '#ffffff'
      let ty = y + 12 * unit
      if (effects.counter.label) {
        ctx.font = `600 ${small}px ${env.font}`
        ctx.fillText(effects.counter.label, x + 16 * unit, ty)
        ty += small * 1.4
      }
      ctx.font = `700 ${size}px ${env.font}`
      ctx.fillText(value, x + 16 * unit, ty)
      ctx.restore()
    }
  }

  // A title card at the start of the stop: over the blurred, darkened picture.
  if (effects.titleCard) {
    const seconds = effects.titleCard.seconds ?? 2.5
    const a = fade({ local: clock.local, hold: Math.min(seconds, clock.hold) }, 0.35, 0.45)
    const text = textIn(effects.titleCard.text, env.language)
    if (a > 0 && text) {
      ctx.save()
      ctx.globalAlpha = a
      if (env.source) {
        ctx.filter = `blur(${Math.round(18 * unit)}px)`
        ctx.drawImage(env.source, 0, 0, W, H)
        ctx.filter = 'none'
      }
      ctx.fillStyle = 'rgba(0, 0, 0, 0.55)'
      ctx.fillRect(0, 0, W, H)
      const size = Math.round(72 * unit)
      ctx.font = `700 ${size}px ${env.font}`
      ctx.textAlign = 'center'
      ctx.textBaseline = 'middle'
      const lines = wrap(ctx, text, W * 0.7)
      const lineH = size * 1.2
      const y0 = H / 2 - ((lines.length - 1) * lineH) / 2
      ctx.fillStyle = env.accent
      ctx.fillRect(W / 2 - 60 * unit, y0 - lineH / 2 - 28 * unit, 120 * unit, 6 * unit)
      ctx.fillStyle = '#ffffff'
      lines.forEach((l, i) => ctx.fillText(l, W / 2, y0 + i * lineH))
      ctx.restore()
    }
  }
}

function measure(ctx: CanvasRenderingContext2D, text: string, size: number, font: string): number {
  ctx.save()
  ctx.font = `600 ${size}px ${font}`
  const w = ctx.measureText(text).width
  ctx.restore()
  return w
}

function wrap(ctx: CanvasRenderingContext2D, text: string, maxWidth: number): string[] {
  const lines: string[] = []
  let line = ''
  for (const word of text.split(/\s+/).filter(Boolean)) {
    const next = line ? `${line} ${word}` : word
    if (line && ctx.measureText(next).width > maxWidth) { lines.push(line); line = word } else line = next
  }
  if (line) lines.push(line)
  return lines
}

/** A label on a dark panel with an accent edge, its left middle at (x, y). */
function label(ctx: CanvasRenderingContext2D, text: string, x: number, y: number, unit: number, font: string, accent?: string): void {
  const size = Math.round(24 * unit)
  ctx.font = `600 ${size}px ${font}`
  ctx.textBaseline = 'middle'
  ctx.textAlign = 'left'
  const pad = 9 * unit
  const w = ctx.measureText(text).width + pad * 2
  const h = size + pad * 1.4
  ctx.fillStyle = 'rgba(0, 0, 0, 0.62)'
  ctx.fillRect(x, y - h / 2, w, h)
  if (accent) { ctx.fillStyle = accent; ctx.fillRect(x, y - h / 2, 4 * unit, h) }
  ctx.fillStyle = '#ffffff'
  ctx.fillText(text, x + pad, y)
}
